import { datadogLogs, type LogsInitConfiguration } from '@datadog/browser-logs';
import { datadogRum, type RumInitConfiguration } from '@datadog/browser-rum';
import { getCurrentCorrelationId } from './correlationId';

type DatadogSite =
  | 'datadoghq.com'
  | 'datadoghq.eu'
  | 'us3.datadoghq.com'
  | 'us5.datadoghq.com'
  | 'ddog-gov.com';

const DEFAULT_SITE: DatadogSite = 'datadoghq.com';
// Use a global variable to track initialization
let isDatadogInitialized = false;

// Add safeguards to prevent multiple initialization
export const initializeDatadog = (): void => {
  // Check for initialization status in both module scope and window object
  if (
    isDatadogInitialized ||
    (typeof window !== 'undefined' &&
      (window as any).__DATADOG_INITIALIZED__) ||
    !process.env.NEXT_PUBLIC_DATADOG_ENABLED
  ) {
    return;
  }

  // Mark as initialized early to prevent race conditions
  isDatadogInitialized = true;
  if (typeof window !== 'undefined') {
    (window as any).__DATADOG_INITIALIZED__ = true;
  }

  // Validate required configuration
  if (!process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN) {
    console.error('Datadog client token is missing!');
    return;
  }

  const site = (process.env.NEXT_PUBLIC_DATADOG_SITE ||
    DEFAULT_SITE) as DatadogSite;
  const env =
    process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV || 'development';
  const serviceName =
    process.env.NEXT_PUBLIC_DATADOG_SERVICE_NAME || 'frontend';

  // Version handling - protect against undefined values
  const version = [
    process.env.version || '0',
    process.env.NEXT_PUBLIC_PRODUCTION_BUILD_NUMBER || '0',
    process.env.NEXT_PUBLIC_STAGING_BUILD_NUMBER || '0',
    process.env.NEXT_PUBLIC_DEV_BUILD_NUMBER || '0',
  ].join('.');

  const commonContext = {
    CorrelationId: getCurrentCorrelationId() || 'not-set',
    env,
    service: serviceName,
    version,
  };

  const logsConfig: LogsInitConfiguration = {
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
    site,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error', 'warn', 'log'],
    forwardReports: 'all',
    sessionSampleRate: 100,
    service: serviceName,
    env,
    version,
    beforeSend: (event) => {
      event.properties = {
        ...(event.properties || {}),
        CorrelationId: getCurrentCorrelationId() || 'not-set',
      };
      return true;
    },
  };

  const envPrefix = env === 'production' ? '' : `${env}.`;

  // Matches: https://[env.]subdomain.breedlogic.com.au/*
  const breedlogicRegex = new RegExp(
    `^https:\\/\\/${envPrefix}([^.]+)\\.breedlogic\\.com\\.au`,
  );

  // Matches: https://as-bl-<service>-[env]-aue-01.azurewebsites.net/*
  const azureRegex = new RegExp(
    `^https:\\/\\/as-bl-[a-z-]+-${env}-aue-01\\.azurewebsites\\.net`,
  );
  const rumConfig: RumInitConfiguration = {
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID || '',
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
    site,
    service: (process.env.NEXT_PUBLIC_DATADOG_SERVICE_NAME || 'unknown-service')
      .trim()
      .toLowerCase(),
    env,
    version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: env === 'production' ? 20 : 100,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [breedlogicRegex, azureRegex],
    beforeSend: (event: any) => {
      event.properties = {
        ...(event.properties || {}),
        CorrelationId: getCurrentCorrelationId() || 'not-set',
      };
      return true;
    },
  };

  // Check if already initialized using SDK methods
  if (typeof window !== 'undefined' && !datadogLogs.getInitConfiguration()) {
    try {
      datadogLogs.init(logsConfig);
      datadogLogs.setGlobalContext(commonContext);
      console.log('Datadog Logs initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Datadog Logs:', error);
    }
  }

  if (typeof window !== 'undefined' && !datadogRum.getInitConfiguration()) {
    try {
      datadogRum.init(rumConfig);
      datadogRum.setGlobalContext(commonContext);

      if (env === 'production') {
        datadogRum.startSessionReplayRecording();
      }
      console.log('Datadog RUM initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Datadog RUM:', error);
    }
  }
};

// Export a function to check if Datadog is initialized
export const checkDatadogInitialized = (): boolean => {
  return (
    isDatadogInitialized ||
    (typeof window !== 'undefined' && !!(window as any).__DATADOG_INITIALIZED__)
  );
};
